import { formatDateString, processRawData } from '../../../../../utils';
import {
  type BlockDataItem,
  BlockDataValueType,
} from '../../../../../components';
import { APPLICATION_TEXT } from '../../../../Monitor/components/lang';
import {
  awardClassificationOptions,
  initialQualificationStatusOptions,
} from '../../../../Application/components';
import { getOptionsLabelClear } from '../../../../Monitor/components';
import type { AcademicQualification as ApplicationAcademicQualification } from '../../../../Application/graphql';
import {
  AR_QUALIFICATION_CHECK_TEXT,
  QUALIFICATION_PAGE_TEXT,
} from '../../../lang';

const ACADEMIC_QUALIFICATION = APPLICATION_TEXT.ACADEMIC_QUALIFICATION;
const defaultValue = AR_QUALIFICATION_CHECK_TEXT.WILL_UPDATE;
const noValueDisplay = AR_QUALIFICATION_CHECK_TEXT.NA;
const chinaVal = 'China'; // same as the value of China passed from programmeChoiceMetadata
export const getAcademicMap = (
  academicQualification: ApplicationAcademicQualification,
): BlockDataItem[] => {
  const {
    programmeName,
    from,
    to,
    currentStudyYear,
    studyMode,
    instructionMedium,
    qualificationMeans,
    awardDate,
    expectUpdateDate,
    withdrawDate,
    qualificationStatus,
    academicCredentialIndicator,
    academicOnlineVerificationCode,
    degreeCertIndicator,
    degreeOnlineVerificationCode,
    gpaIndicator: noGPAIndictor,
    rankIndicator: noRankIndicator,
    studyField,
    institution,
    degreeLevel,
    officialMark,
    awardClassification,
    officialClassRanking,
    marks,
    outOfGpa,
    outOfRank,
    certificates,
    transcripts,
    markExplanations,
    classRankProofs,
    otherDocuments,
    presentIndicator,
  } = academicQualification ?? {};

  const { name: institutionName, country: institutionCountry } =
    institution ?? {};
  // converted country
  const isChina = institutionCountry === chinaVal;
  const hasMark = Boolean(marks) && marks.length > 0;
  const mark = `${officialMark} ${AR_QUALIFICATION_CHECK_TEXT.OUT_OF} ${outOfGpa}`;
  let officialGPA;
  let calculatedGPA;

  // if have gpa, but need to know the gpa is official gpa or calculated;
  if (!noGPAIndictor) {
    if (hasMark) {
      calculatedGPA = mark;
    } else {
      officialGPA = mark;
    }
  }

  const rank = noRankIndicator
    ? undefined
    : `${officialClassRanking} ${AR_QUALIFICATION_CHECK_TEXT.OUT_OF} ${outOfRank}`;

  const classification = getOptionsLabelClear(
    awardClassificationOptions,
    awardClassification,
  ) as string;

  const status = getOptionsLabelClear(
    initialQualificationStatusOptions,
    qualificationStatus,
  );

  const academicMap: BlockDataItem[] = [
    {
      label: ACADEMIC_QUALIFICATION.NAME_OF_PROGRAMME_AWARD,
      value: programmeName,
    },
    {
      label: ACADEMIC_QUALIFICATION.INSTITUTION_NAME,
      value: institutionName,
    },
    {
      label: ACADEMIC_QUALIFICATION.INSTITUTION_COUNTRY,
      value: institutionCountry,
    },
    {
      label: ACADEMIC_QUALIFICATION.DEGREE_LEVEL,
      value: processRawData(degreeLevel),
    },
    {
      label: ACADEMIC_QUALIFICATION.FROM,
      value: formatDateString(from),
    },
    {
      label: ACADEMIC_QUALIFICATION.TO,
      value: formatDateString(to),
    },
    {
      label: ACADEMIC_QUALIFICATION.CURRENT_STUDY_YEAR,
      value: presentIndicator ? currentStudyYear : undefined,
      defaultValue: noValueDisplay,
    },
    {
      label: ACADEMIC_QUALIFICATION.MODE_OF_STUDY,
      value: processRawData(studyMode),
    },
    {
      label: ACADEMIC_QUALIFICATION.MEDIUM_OF_INSTRUCTION,
      value: processRawData(instructionMedium),
    },
    {
      label: ACADEMIC_QUALIFICATION.FIELD_OF_STUDY,
      value: processRawData(studyField),
    },
    {
      label: ACADEMIC_QUALIFICATION.MEDIUM_OF_QUALIFICATION,
      value: processRawData(qualificationMeans),
    },
    {
      label: ACADEMIC_QUALIFICATION.QUALIFICATION_STATUS,
      value: status,
    },
    {
      label: ACADEMIC_QUALIFICATION.DATE_OF_AWARD,
      value: formatDateString(awardDate),
      defaultValue: presentIndicator ? defaultValue : noValueDisplay,
    },
    {
      label: ACADEMIC_QUALIFICATION.EXPECTED_DATE,
      value: formatDateString(expectUpdateDate),
      defaultValue: noValueDisplay,
    },
    {
      label: ACADEMIC_QUALIFICATION.WITHDRAWAL_DATE,
      value: formatDateString(withdrawDate),
      defaultValue: noValueDisplay,
    },
    ...(isChina
      ? [
          {
            label: ACADEMIC_QUALIFICATION.DEGREE_VERIFICATION,
            value: academicCredentialIndicator
              ? academicOnlineVerificationCode
              : undefined,
            defaultValue,
          },
          {
            label: ACADEMIC_QUALIFICATION.ONLINE_VERIFICATION,
            value: degreeCertIndicator
              ? degreeOnlineVerificationCode
              : undefined,
            defaultValue,
          },
        ]
      : []),
    {
      label: ACADEMIC_QUALIFICATION.OFFICIAL_AWARD_GPA,
      value: officialGPA,
      defaultValue: noValueDisplay,
    },
    {
      label: `${ACADEMIC_QUALIFICATION.CALCULATED_GPA}`,
      value: calculatedGPA,
      defaultValue: noValueDisplay,
    },
    {
      label: ACADEMIC_QUALIFICATION.CLASS_RANK,
      value: rank,
      defaultValue: noValueDisplay,
    },
    {
      label: ACADEMIC_QUALIFICATION.AWARD_CLASSIFICATION,
      value: classification,
      defaultValue: noValueDisplay,
    },
    {
      label: QUALIFICATION_PAGE_TEXT.CERTIFICATE,
      value: certificates,
      valueType: BlockDataValueType.FILE_ARRAY,
    },
    {
      label: QUALIFICATION_PAGE_TEXT.TRANSCRIPT,
      value: transcripts,
      valueType: BlockDataValueType.FILE_ARRAY,
    },

    {
      label: QUALIFICATION_PAGE_TEXT.MARK_EXPLANATION,
      value: markExplanations,
      valueType: BlockDataValueType.FILE_ARRAY,
    },

    {
      label: QUALIFICATION_PAGE_TEXT.CLASS_RANKING_PROOF,
      value: classRankProofs,
      valueType: BlockDataValueType.FILE_ARRAY,
    },

    {
      label: QUALIFICATION_PAGE_TEXT.OTHER_DOCUMENTS,
      value: otherDocuments,
      valueType: BlockDataValueType.FILE_ARRAY,
      defaultValue: noValueDisplay,
    },
  ];
  return academicMap;
};

export const getApplicantAcademicMap = (
  academicQualification: ApplicationAcademicQualification,
): BlockDataItem[] => {
  const {
    programmeName,
    academicOnlineVerificationCode,
    degreeOnlineVerificationCode,
    institution,
    academicCredentialIndicator,
    degreeCertIndicator,
  } = academicQualification;

  const { name: institutionName, country: institutionCountry } =
    institution ?? {};

  const isChinese = institutionCountry === chinaVal;

  const academicMap: BlockDataItem[] = [
    {
      label: ACADEMIC_QUALIFICATION.NAME_OF_PROGRAMME_AWARD,
      value: programmeName,
    },
    {
      label: ACADEMIC_QUALIFICATION.INSTITUTION_NAME,
      value: institutionName,
    },
    {
      label: ACADEMIC_QUALIFICATION.DEGREE_VERIFICATION,
      value: academicCredentialIndicator
        ? academicOnlineVerificationCode
        : undefined,
      defaultValue: isChinese ? defaultValue : noValueDisplay,
    },
    {
      label: ACADEMIC_QUALIFICATION.ONLINE_VERIFICATION,
      value: degreeCertIndicator ? degreeOnlineVerificationCode : undefined,
      defaultValue: isChinese ? defaultValue : noValueDisplay,
    },
  ];
  return academicMap;
};
